import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { getIdTokenResult } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions, selectedAdminEnvironment, getSelectedAdminEnvironmentKey, ADMIN_ENV_STORAGE_KEY } from "../config/firebase";
import { canAccessAdminPortal } from "../utils/adminRoles";
import { readAdminAuditPage } from "./auditLogPaging";
import "./styles/AuditLogs.css";

const EMPTY_FILTERS = { restaurantId: "", actorUid: "", action: "" };
const APPLIED_FILTERS = { restaurantId: null, actorUid: null, action: null };
const supportRole = (token) => {
	const role = token?.claims?.role;
	return typeof role === "string" && role === role.trim() && canAccessAdminPortal(role) ? role : null;
};
const environmentScope = () => {
	const { key, projectId, firebaseConfig } = selectedAdminEnvironment;
	return projectId && getSelectedAdminEnvironmentKey() === key && firebaseConfig?.projectId === projectId &&
		functions.app?.options?.projectId === projectId && auth.app?.options?.projectId === projectId ? `${key}:${projectId}` : null;
};

const AuditLogList = ({ user, scope, projectId }) => {
	const [result, setResult] = useState(null);
	const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
	const [appliedFilters, setAppliedFilters] = useState(APPLIED_FILTERS);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const generation = useRef(0);
	const alive = useRef(false);
	const confirmedRole = useRef(null);

	const loadLogs = useCallback(async (nextFilters, cursors = [null]) => {
		const ticket = ++generation.current;
		const instance = functions;
		const uid = user.uid;
		const filters = { ...nextFilters };
		const history = [...cursors];
		const active = () => alive.current && generation.current === ticket;
		const current = () => active() && auth.currentUser === user && user.uid === uid &&
			functions === instance && environmentScope() === scope;
		setLoading(true);
		setError("");
		setResult(null);
		const checkRole = (token, expectedRole = confirmedRole.current) => {
			const role = supportRole(token);
			if (!role || (expectedRole !== null && role !== expectedRole)) {
				confirmedRole.current = null;
				setDraftFilters(EMPTY_FILTERS);
				setAppliedFilters(APPLIED_FILTERS);
				setError(role ? "Support role changed. Reload audit logs." : "Scerv admin access is required.");
				return null;
			}
			return role;
		};
		try {
			if (!current()) return;
			const token = await getIdTokenResult(user, true);
			if (!current()) return;
			const role = checkRole(token);
			if (!role) return;
			const listLogs = httpsCallable(instance, "listScervAdminAuditLogs");
			const response = await listLogs({
				projectId,
				pageSize: 100,
				...filters,
				cursor: history[history.length - 1],
			});
			if (!current()) return;
			const page = readAdminAuditPage(response?.data, { projectId, requestingAdminUid: uid, filters, cursors: history });
			const latestToken = await getIdTokenResult(user, true);
			if (!current() || !checkRole(latestToken, role)) return;
			confirmedRole.current = role;
			setResult({ ...page, user, uid, scope, filters, cursors: history });
		} catch (err) {
			if (current()) setError("Failed to load audit logs. The response could not be confirmed; try again.");
		} finally {
			if (active()) {
				if (!current()) setError("Account or environment changed. Reload audit logs.");
				setLoading(false);
			}
		}
	}, [scope, user, projectId]);

	useEffect(() => {
		alive.current = true;
		setDraftFilters(EMPTY_FILTERS);
		setAppliedFilters(APPLIED_FILTERS);
		loadLogs(APPLIED_FILTERS);
		return () => { alive.current = false; generation.current += 1; };
	}, [loadLogs]);

	const page = result?.user === user && result.uid === user.uid && auth.currentUser === user &&
		result.scope === scope && environmentScope() === scope ? result : null;
	const logs = page?.logs || [];

	const updateFilter = (field, value) => {
		setDraftFilters((prev) => ({ ...prev, [field]: value }));
	};

	const applyFilters = (event) => {
		event.preventDefault();
		const filters = Object.fromEntries(Object.entries(draftFilters).map(([key, value]) => [key, value.trim() || null]));
		setAppliedFilters(filters);
		loadLogs(filters);
	};

	return (
		<div className="audit-log-container">
			<div className="audit-log-header">
				<h1>Admin Audit Logs</h1>
				<button type="button" className="audit-log-icon-button" aria-label="Reload audit logs" title="Reload audit logs"
					disabled={loading} onClick={() => loadLogs(appliedFilters)}>&#8635;</button>
			</div>

			<form className="audit-log-filters" onSubmit={applyFilters}>
				<input
					aria-label="Restaurant ID"
					value={draftFilters.restaurantId}
					onChange={(event) => updateFilter("restaurantId", event.target.value)}
					placeholder="Restaurant ID"
				/>
				<input
					aria-label="Actor UID"
					value={draftFilters.actorUid}
					onChange={(event) => updateFilter("actorUid", event.target.value)}
					placeholder="Actor UID"
				/>
				<input
					aria-label="Action"
					value={draftFilters.action}
					onChange={(event) => updateFilter("action", event.target.value)}
					placeholder="Action"
				/>
				<button type="submit" disabled={loading}>
					{loading ? "Loading..." : "Apply"}
				</button>
			</form>

			<dl className="audit-log-applied" aria-label="Applied filters">
				<div><dt>Restaurant</dt><dd>{appliedFilters.restaurantId || "All"}</dd></div>
				<div><dt>Actor</dt><dd>{appliedFilters.actorUid || "All"}</dd></div>
				<div><dt>Action</dt><dd>{appliedFilters.action || "All"}</dd></div>
			</dl>
			{error && <p role="alert" className="audit-log-error">{error}</p>}
			<nav className="audit-log-pagination" aria-label="Audit pages">
				<button type="button" className="audit-log-icon-button" aria-label="Previous page" title="Previous page"
					disabled={loading || !page || page.cursors.length < 2}
					onClick={() => page && loadLogs(page.filters, page.cursors.slice(0, -1))}>&#8592;</button>
				<p role="status">{loading ? "Loading audit logs..." : page ?
					page.limitedHistory ? `Recent records (limited history) | ${logs.length} ${logs.length === 1 ? "record" : "records"}` :
						`Page ${page.cursors.length} | ${logs.length} ${logs.length === 1 ? "record" : "records"}${page.nextCursor === null ? " | End of results" : ""}` : ""}</p>
				<button type="button" className="audit-log-icon-button" aria-label="Next page" title="Next page"
					disabled={loading || !page || page.nextCursor === null}
					onClick={() => page?.nextCursor && loadLogs(page.filters, [...page.cursors, page.nextCursor])}>&#8594;</button>
			</nav>

			<div className="audit-log-table-scroll" role="region" aria-label="Audit records" tabIndex={0}>
				<table className="audit-log-table">
					<thead>
						<tr>
							<th>When</th>
							<th>Action</th>
							<th>Actor (top-level)</th>
							<th>Audit facts (allowlisted)</th>
						</tr>
					</thead>
					<tbody>
						{logs.map((log) => (
							<tr key={log.id}>
								<td>{log.times.length ? log.times.map((time) => <div key={time.field}>{time.field}: {time.value}</div>) : "Not recorded"}</td>
								<td>{log.action}</td>
								<td className="audit-mono">{log.actorUid}</td>
								<td>
									{log.action === "drawer_incident_continued" && <>
										<p>Drawer continuity incident: review the previous and next commands together. Continuity is not proof of an open drawer; a staff report is not sensor verification. Do not replay the previous command or repeat a payment, refund or cash adjustment.</p>
										{log.facts.payload.financialReviewRequired === true && <p><strong>Financial review required for the previous operation.</strong></p>}
									</>}
									<pre>{JSON.stringify(log.facts, null, 2)}</pre>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{!loading && !error && page && logs.length === 0 && <p>{page.limitedHistory ?
				"No matching audit records in recent history." : "No matching audit records."}</p>}
		</div>
	);
};

const AuditLogs = () => {
	const [user, loading, error] = useAuthState(auth);
	const session = useRef({ user: null, revision: 0 });
	if (session.current.user !== user) session.current = { user, revision: session.current.revision + 1 };
	const [, refreshEnvironment] = useState(0);
	useEffect(() => {
		const changed = (event) => {
			if (event.key === ADMIN_ENV_STORAGE_KEY || event.key === null) refreshEnvironment((value) => value + 1);
		};
		window.addEventListener("storage", changed);
		return () => window.removeEventListener("storage", changed);
	}, []);
	const scope = environmentScope();
	if (loading) return <p role="status">Checking support access...</p>;
	if (error || !user || auth.currentUser !== user) return <p role="alert">Sign in with an authorized support account.</p>;
	if (!scope) return <p role="alert">Audit environment does not match the active Firebase project. Reload before continuing.</p>;
	return <AuditLogList key={`${scope}:${user.uid}:${session.current.revision}`} user={user} scope={scope}
		projectId={selectedAdminEnvironment.projectId} />;
};

export default AuditLogs;
